import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { 
  TrendingUp, TrendingDown, Minus, Flame, Droplets, Utensils, 
  Brain, Trophy, Target, Sparkles, ChevronRight, Calendar,
  Heart, Zap, X
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WeeklyProgressReport = ({ isOpen, onClose }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeader = () => ({ 
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } 
  });

  useEffect(() => {
    if (isOpen) {
      fetchReport();
    }
  }, [isOpen]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/progress/weekly-report`, getAuthHeader());
      setReport(res.data);
    } catch (err) {
      console.error("Error fetching report:", err);
      toast.error("Couldn't load your weekly report");
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === "up") return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === "down") return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-amber-500";
    if (score >= 40) return "text-orange-500";
    return "text-red-500";
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "Amazing!";
    if (score >= 60) return "Great Progress";
    if (score >= 40) return "Keep Going";
    return "Let's Improve";
  };

  const getMoodEmoji = (mood) => {
    if (mood >= 4.5) return "😄";
    if (mood >= 3.5) return "🙂";
    if (mood >= 2.5) return "😐";
    return "😔";
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options = { month: 'short', day: 'numeric' };
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-500" />
            Weekly Progress Report
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mb-4" />
            <p className="text-sm text-muted-foreground">Generating your report...</p>
          </div>
        ) : report ? (
          <div className="space-y-5">
            {/* Week Range */}
            <p className="text-sm text-muted-foreground text-center">
              {formatDateRange(report.week_range.start, report.week_range.end)}
            </p>

            {/* Overall Score */}
            <div className="p-5 rounded-2xl bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-amber-500/10 border border-purple-500/20 text-center">
              <p className="text-sm text-muted-foreground mb-1">Your Wellness Score</p>
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className={`text-5xl font-bold ${getScoreColor(report.overall_score)}`}>
                  {report.overall_score}
                </span>
                <span className="text-2xl text-muted-foreground">/100</span>
              </div>
              <p className={`text-sm font-medium ${getScoreColor(report.overall_score)}`}>
                {getScoreLabel(report.overall_score)}
              </p>
              <Progress value={report.overall_score} className="h-2 mt-3" />
            </div>

            {/* Highlights */}
            {report.highlights && report.highlights.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  This Week's Highlights
                </h4>
                <div className="grid gap-2">
                  {report.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <span className="text-lg">{h.emoji}</span>
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-400">{h.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Meditation */}
              <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg">🧘🏾</span>
                  {getTrendIcon(report.meditation.trend)}
                </div>
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {report.meditation.days_this_week}
                  <span className="text-sm font-normal text-muted-foreground"> days</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {report.meditation.minutes_this_week} min meditated
                </p>
                {report.meditation.current_streak > 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    <Flame className="w-3 h-3 text-orange-500" />
                    <span className="text-xs text-orange-600">{report.meditation.current_streak} day streak</span>
                  </div>
                )}
              </div>

              {/* Nutrition */}
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
                <div className="flex items-center justify-between mb-2">
                  <Utensils className="w-5 h-5 text-green-600" />
                  {getTrendIcon(report.nutrition.trend)}
                </div>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {report.nutrition.meals_logged}
                  <span className="text-sm font-normal text-muted-foreground"> meals</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {report.nutrition.days_tracked} days tracked
                </p>
                {report.nutrition.avg_calories > 0 && (
                  <p className="text-xs text-green-600 mt-1">
                    ~{report.nutrition.avg_calories} cal/day
                  </p>
                )}
              </div>

              {/* Hydration */}
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20">
                <div className="flex items-center justify-between mb-2">
                  <Droplets className="w-5 h-5 text-blue-600" />
                  <span className="text-xs text-muted-foreground">Goal: 8/day</span>
                </div>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {report.hydration.avg_per_day}
                  <span className="text-sm font-normal text-muted-foreground"> avg</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {report.hydration.total_glasses} glasses total
                </p>
                <Progress 
                  value={(report.hydration.avg_per_day / 8) * 100} 
                  className="h-1.5 mt-2" 
                />
              </div>

              {/* Mood & Energy */}
              <div className="p-3 rounded-xl bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/20">
                <div className="flex items-center justify-between mb-2">
                  <Heart className="w-5 h-5 text-pink-600" />
                  {report.wellness.avg_mood && (
                    <span className="text-lg">{getMoodEmoji(report.wellness.avg_mood)}</span>
                  )}
                </div>
                {report.wellness.avg_mood ? (
                  <>
                    <p className="text-2xl font-bold text-pink-600 dark:text-pink-400">
                      {report.wellness.avg_mood}
                      <span className="text-sm font-normal text-muted-foreground">/5</span>
                    </p>
                    <p className="text-xs text-muted-foreground">Avg mood score</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-pink-600">No mood logs</p>
                    <p className="text-xs text-muted-foreground">Track your daily mood!</p>
                  </>
                )}
                {report.wellness.avg_energy && (
                  <div className="flex items-center gap-1 mt-1">
                    <Zap className="w-3 h-3 text-amber-500" />
                    <span className="text-xs text-amber-600">Energy: {report.wellness.avg_energy}/5</span>
                  </div>
                )}
              </div>
            </div>

            {/* Challenges */}
            <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-600" />
                  <span className="font-medium">Challenges</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-center">
                    <p className="text-lg font-bold text-amber-600">{report.challenges.completed_this_week}</p>
                    <p className="text-[10px] text-muted-foreground">Completed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-orange-600">{report.challenges.active}</p>
                    <p className="text-[10px] text-muted-foreground">Active</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Goal Progress */}
            {report.goal_progress && (
              <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500/10 to-violet-500/10 border border-purple-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-purple-600" />
                  <span className="font-medium">Weight Goal Progress</span>
                </div>
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <p className="text-xl font-bold text-purple-600">{report.goal_progress.current_weight}</p>
                    <p className="text-xs text-muted-foreground">Current (lbs)</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-purple-400" />
                  <div className="text-center">
                    <p className="text-xl font-bold text-pink-600">{report.goal_progress.goal_weight}</p>
                    <p className="text-xs text-muted-foreground">Goal (lbs)</p>
                  </div>
                </div>
                <p className="text-xs text-center text-muted-foreground mt-2">
                  {report.goal_progress.remaining} lbs to {report.goal_progress.direction}
                </p>
              </div>
            )}

            {/* Tips */}
            {report.tips && report.tips.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <Brain className="w-4 h-4 text-blue-500" />
                  Tips for Next Week
                </h4>
                {report.tips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <span className="text-blue-500">💡</span>
                    <p className="text-sm text-blue-700 dark:text-blue-400">{tip}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Close Button */}
            <Button 
              onClick={onClose}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
            >
              Keep Up the Great Work!
            </Button>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Unable to load report</p>
            <Button variant="outline" onClick={fetchReport} className="mt-4">
              Try Again
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WeeklyProgressReport;
